import { estimateProjectCost, isProjectCostProfile } from '@/lib/projectCostEstimate';
import {
  ShoppingBag,
  Star,
  UtensilsCrossed,
  Home,
  Rocket,
  Smartphone,
  Clapperboard,
  Briefcase,
  Sparkles,
  MessageSquare,
  TrendingUp,
  DollarSign,
  Palette,
  ClipboardCheck,
  FileText,
  Presentation,
  Youtube,
  Instagram,
  UserSearch,
  HandCoins,
  GraduationCap,
  Megaphone,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { toolsData } from './tools.data.mjs';

export type ToolKind = 'ai' | 'deterministic';
export type ToolModel = 'gemini-3.1-flash-lite';
export type ToolCategory = 'content' | 'design' | 'business' | 'local' | 'career';
export type ToolInputType = 'text' | 'textarea' | 'select';

export interface ToolInput {
  name: string;
  label: string;
  type: ToolInputType;
  placeholder?: string;
  options?: string[];
  /** Display labels for `options` when the stored values are ids. */
  optionLabels?: string[];
  required?: boolean;
}

export interface ToolFaq {
  q: string;
  a: string;
}

export interface ToolExample {
  summary: string;
  results: string[];
}

export interface ToolSeo {
  title: string;
  description: string;
  keywords: string[];
}

export interface ToolDefinition {
  id: string;
  /** YYYY-MM-DD of the last meaningful change to this tool's page (sitemap lastmod). */
  contentUpdated?: string;
  kind: ToolKind;
  model?: ToolModel;
  category: ToolCategory;
  segment: string;
  icon: string;
  title: string;
  tagline: string;
  intro: string;
  inputs: ToolInput[];
  seo: ToolSeo;
  howItWorks: string[];
  useCases: string[];
  faq: ToolFaq[];
  example: ToolExample;
  sitemapPriority: string;
}

export const tools = toolsData as ToolDefinition[];

/** lucide-react ikon adı -> bileşen eşlemesi (tools.data.mjs içindeki `icon` ile eşleşir). */
export const toolIcons: Record<string, LucideIcon> = {
  ShoppingBag,
  Star,
  UtensilsCrossed,
  Home,
  Rocket,
  Smartphone,
  Clapperboard,
  Briefcase,
  Sparkles,
  MessageSquare,
  TrendingUp,
  DollarSign,
  Palette,
  ClipboardCheck,
  FileText,
  Presentation,
  Youtube,
  Instagram,
  UserSearch,
  HandCoins,
  GraduationCap,
  Megaphone,
};

export function getToolIcon(tool: ToolDefinition): LucideIcon {
  return toolIcons[tool.icon] ?? Wrench;
}

export const categoryLabels: Record<ToolCategory, string> = {
  content: 'Metin & İçerik',
  design: 'Tasarım & Marka',
  business: 'İş & Strateji',
  local: 'Yerel İşletme',
  career: 'Kariyer',
};

/** Görüntülenme sırası. */
export const categoryOrder: ToolCategory[] = ['content', 'career', 'business', 'local', 'design'];

export const getAllTools = (): ToolDefinition[] => tools;

export const getToolById = (id: string): ToolDefinition | undefined =>
  tools.find((t) => t.id === id);

export const getToolsByCategory = (category: ToolCategory): ToolDefinition[] =>
  tools.filter((t) => t.category === category);

/** Bir aracın aynı kategorideki diğerleri (iç linkleme için). */
export const getRelatedTools = (tool: ToolDefinition, limit = 3): ToolDefinition[] =>
  tools.filter((t) => t.id !== tool.id && t.category === tool.category).slice(0, limit);

export const TOOL_IDS = tools.map((t) => t.id);

/**
 * Client-side (AI gerektirmeyen) deterministik araçların hesap fonksiyonları.
 * Anahtar = tool id.
 */
export const deterministicGenerators: Record<string, (inputs: Record<string, string>) => string[]> = {
  'roi-calculator': (inputs) => {
    const budget = parseInt((inputs.budget || '').replace(/\D/g, ''), 10) || 10000;
    return [
      `E-posta pazarlama ROI: ₺${Math.round(budget * 4.2).toLocaleString('tr-TR')} — En yüksek getiri oranı`,
      `Sosyal medya reklam getirisi: ₺${Math.round(budget * 2.1).toLocaleString('tr-TR')} — ROAS: 2.1x`,
      `SEO yatırımı (6 ay): organik trafikte ~%${Math.round(budget * 0.02)} artış — uzun vadeli değer`,
      `Dijital pazarlama tahmini geri dönüş: ₺${Math.round(budget * 1.35).toLocaleString('tr-TR')}`,
    ];
  },
  'project-cost-estimator': (inputs) =>
    estimateProjectCost(
      inputs.project || '',
      'tr',
      isProjectCostProfile(inputs.profile) ? inputs.profile : null,
    ).lines,
  'color-palette-generator': (inputs) => {
    const m = (inputs.mood || 'markanız').trim();
    return [
      `#0A0A0A • #F5F5F5 • #3B82F6 • #10B981 — "${m}" için minimal & temiz`,
      `#1A1A2E • #E94560 • #16213E • #0F3460 — "${m}" için cesur & dinamik`,
      `#FAF3E0 • #2C3639 • #3F4E4F • #A27B5C — "${m}" için doğal & premium`,
      `#0D1117 • #58A6FF • #238636 • #F78166 — "${m}" için teknolojik & modern`,
    ];
  },
};
